import { RISK_MEASURE_URL, RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import {
  RISK_EXTENSION_GAP_STATUS_UPDATES,
  RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
} from '@ctw/shared/api/fhir/system-urls';
import { searchFirstPartyRecords } from '@ctw/shared/api/fqs-rest/search-helpers';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { Env } from '@ctw/shared/utils/environments';
import { QUERY_KEY_PATIENT_RISK_GAPS } from '@ctw/shared/utils/query-keys';

/*
Gets patient risk gaps with UI exclusions applied.
*/
export const usePatientRiskGapsWithUIExclusions = () => {
  const builderConfig = useBuilderConfig();

  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_RISK_GAPS,
    queryFn: async ({ requestContext, ctwFetch, patient }) => {
      if (!patient.UPID) {
        return [];
      }
      const riskGaps = await searchRiskGaps({
        ctwFetch,
        env: requestContext.env,
        builderId: requestContext.builderId,
        authToken: requestContext.authToken,
        searchParams: {
          upid: patient.UPID ?? '',
          _count: '200',
        },
      });

      // Filter out risk gaps with excluded HCC codes
      const excludedHccCodes = builderConfig.riskGaps?.uiExcludedHccCodes ?? [];
      if (excludedHccCodes.length > 0) {
        return riskGaps.filter((riskGap) => {
          return !excludedHccCodes.includes(riskGap.hccCode);
        });
      }

      return riskGaps;
    },
  });
};

interface SearchRiskGapsOptions {
  ctwFetch: CTWState['ctwFetch'];
  authToken: string;
  env: Env;
  builderId: string;
  searchParams: Record<string, string>;
}

async function searchRiskGaps({
  ctwFetch,
  env,
  builderId,
  authToken,
  searchParams,
}: SearchRiskGapsOptions) {
  const measureReports = await searchFirstPartyRecords(
    ctwFetch,
    {
      env: env,
      builderId: builderId,
      authToken: authToken,
    },
    'MeasureReport',
    searchParams,
  );

  // Filter the MeasureReport Resources to those with a Measure of RISK_MEASURE_URL (otherwise we'll
  // get care gaps mixed in!)
  const riskMeasureReports = measureReports.filter((report) => {
    const lastStatusUpdateExtension = report.extension
      ?.find((extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATES)
      ?.extension?.at(-1);
    const isInvalidGap =
      lastStatusUpdateExtension?.extension?.find(
        (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
      )?.valueCodeableConcept?.coding?.[0]?.code === 'invalid-gap';

    if (!isInvalidGap && report.measure === RISK_MEASURE_URL) {
      return true;
    }

    return false;
  });

  return riskMeasureReports.map((report) => {
    return RiskGapModel.fromUpidAndMeasureReport(searchParams.upid, report);
  });
}

interface GetPatientRiskGapArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
}

export const getPatientRiskGaps = async ({
  ctwFetch,
  requestContext,
  upid,
}: GetPatientRiskGapArgs) => {
  const riskGaps = await searchRiskGaps({
    ctwFetch,
    env: requestContext.env,
    authToken: requestContext.authToken,
    builderId: requestContext.builderId,
    searchParams: {
      upid,
      _count: '500',
    },
  });

  // Note: This function doesn't have access to builder config, so filtering
  // will need to be applied by the caller if needed
  return riskGaps;
};
